import { useCallback, useMemo, useRef, useState } from 'react'
import { Link, useBlocker, useNavigate, useSearchParams } from 'react-router'
import posthog from 'posthog-js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Layers, Loader2, Package, Rows3, Upload } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { AppShell } from '@/components/AppShell'
import { ObjectTypeLabel } from '@/components/ObjectTypeLabel'
import { ObjectAncestorsList } from '@/components/ObjectAncestorsList'
import { objectCrumbs } from '@/components/objectCrumbs'
import { SearchSelect } from '@/components/SearchSelect'
import { WizardSteps } from '@/components/WizardSteps'
import { FormSkeleton } from '@/components/FormSkeleton'
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog'
import { newObjectUrl } from '@/components/CreateObjectButton'
import { CreativeKindPicker } from '@/components/creative-upload/CreativeKindPicker'
import { FileDropzone } from '@/components/creative-upload/FileDropzone'
import { BulkCreativeTable } from '@/components/creative-upload/BulkCreativeTable'
import { BulkEditToolbar } from '@/components/creative-upload/BulkEditToolbar'
import { TagPasteDialog } from '@/components/creative-upload/TagPasteDialog'
import { useNetwork } from '@/contexts/NetworkContext'
import { useLineItemAncestors } from '@/hooks/useLineItemAncestors'
import { apiErrorDescription } from '@/services/core/apiError'
import { AssetsApi } from '@/services/assets/assetsApi'
import { CreativesApi, type ThirdPartyTagRow } from '@/services/creatives/creativesApi'
import { extractTags } from '@/services/creatives/tagExtraction'
import { LineItemsApi } from '@/services/lineItems/lineItemsApi'
import { AxProductsApi } from '@/services/earnings/axProductsApi'
import { LayoutsApi } from '@/services/sites/layoutsApi'
import {
  CREATIVE_KINDS,
  KIND_ACCEPT,
  creativeInputFor,
  draftProblem,
  layoutCategoryKey,
  layoutKinds,
  nameFromFileName,
  suggestLayout,
  type BulkCreativeDraft,
  type BulkCreativeOwner,
  type CreativeKind,
} from '@/services/creatives/bulkUpload'

const posthogConfigured = Boolean(
  import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_PUBLIC_POSTHOG_HOST
)

/** Files uploaded at once; the rest wait their turn. */
const UPLOAD_CONCURRENCY = 4

/**
 * Stands in for "any size" when no product limits it. One shared array, so the
 * table's columns are not rebuilt — and its inputs remounted — on every render.
 */
const ANY_DIMENSIONS: number[][] = []

function newCreativeId(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 16)
}

/** Whether a picked file is the kind of media the upload is for. */
function fileMatchesKind(file: File, kind: CreativeKind): boolean {
  if (kind === 'IMAGE') return file.type.startsWith('image/')
  if (kind === 'VIDEO') return file.type.startsWith('video/')
  if (kind === 'NATIVE') return file.type.startsWith('image/') || file.type.startsWith('video/')
  return true
}

/**
 * Creates many creatives from a batch of files. The user picks what kind of
 * creative they are making, uploads the files, adjusts names, categories,
 * layouts and links — one by one or all at once — and creates them in one go.
 *
 * Files are uploaded unattached; the server hands each asset to the creative
 * that is saved with it, so leaving the page half-way creates nothing.
 */
export function CreativeBulkUploadPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentNetwork } = useNetwork()

  const lineItemId = searchParams.get('lineitem') ?? ''
  const advertiserId = searchParams.get('advertiser') ?? ''
  const hasOwner = !!lineItemId || !!advertiserId
  const marketplace = !!currentNetwork?.marketplacePlatform

  const ancestors = useLineItemAncestors(lineItemId || null, { advertiser: { id: advertiserId } })
  const crumbs = objectCrumbs(ancestors, t('creativeUpload.title'))

  // The same cache entry useLineItemAncestors reads.
  const { data: lineItem } = useQuery({
    queryKey: ['lineitem', lineItemId],
    queryFn: () => LineItemsApi.getById(lineItemId),
    enabled: !!lineItemId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  const productId = lineItem?.axProductId ?? ''
  const { data: product } = useQuery({
    queryKey: ['axproduct', productId],
    queryFn: () => AxProductsApi.getById(productId),
    enabled: !!productId,
    staleTime: 5 * 60 * 1000,
  })

  // Given a line item the server narrows the layouts to its product's.
  const { data: layouts = [], isLoading: layoutsLoading } = useQuery({
    queryKey: ['layouts', 'for-creatives', lineItemId, marketplace],
    queryFn: () =>
      LayoutsApi.getForCreatives({
        lineItemId: lineItemId || undefined,
        onlyOnAxProducts: marketplace,
      }),
    enabled: hasOwner,
  })

  const { data: lineItemOptions = [], isLoading: lineItemsLoading } = useQuery({
    queryKey: ['lineitems', 'bulk-upload-picker'],
    queryFn: () => LineItemsApi.getAll({ objectState: 'ACTIVE' }),
    enabled: !hasOwner,
  })

  const [kind, setKind] = useState<CreativeKind | null>(null)
  const [drafts, setDrafts] = useState<BulkCreativeDraft[]>([])
  const [selection, setSelection] = useState<BulkCreativeDraft[]>([])
  const [parsing, setParsing] = useState(false)

  const layoutsByKind = useMemo(() => {
    const byKind = Object.fromEntries(
      CREATIVE_KINDS.map((k) => [k, [] as typeof layouts])
    ) as Record<CreativeKind, typeof layouts>
    for (const layout of layouts) {
      for (const k of layoutKinds(layout)) byKind[k].push(layout)
    }
    return byKind
  }, [layouts])
  const layoutCounts = useMemo(
    () =>
      Object.fromEntries(CREATIVE_KINDS.map((k) => [k, layoutsByKind[k].length])) as Record<
        CreativeKind,
        number
      >,
    [layoutsByKind]
  )
  const kindLayouts = kind ? layoutsByKind[kind] : []

  const categoryLabel = useCallback(
    (category: string) =>
      category.startsWith('detected:')
        ? t(`creativeUpload.detected.${category.slice('detected:'.length)}`, {
            defaultValue: category.slice('detected:'.length),
          })
        : category,
    [t]
  )

  const patchDraft = useCallback((id: string, patch: Partial<BulkCreativeDraft>) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)))
  }, [])
  const patchDrafts = useCallback((patches: Record<string, Partial<BulkCreativeDraft>>) => {
    setDrafts((prev) => prev.map((d) => (patches[d.id] ? { ...d, ...patches[d.id] } : d)))
  }, [])
  const removeDraft = useCallback((id: string) => {
    setDrafts((prev) => prev.filter((d) => d.id !== id))
    setSelection((prev) => prev.filter((d) => d.id !== id))
  }, [])

  /** Puts a draft on its best layout once its file or tag is known. */
  const withLayout = (draft: BulkCreativeDraft): BulkCreativeDraft => {
    const layout = suggestLayout(kindLayouts, draft)
    return {
      ...draft,
      layoutId: layout?.id ?? '',
      category: layout ? layoutCategoryKey(layout) : '',
    }
  }

  const blankDraft = (fileName: string): BulkCreativeDraft => ({
    id: newCreativeId(),
    fileName,
    status: 'uploading',
    progress: 0,
    name: nameFromFileName(fileName),
    width: 0,
    height: 0,
    halfSize: false,
    category: '',
    layoutId: '',
    url: '',
  })

  const uploadFiles = async (files: File[], uploadKind: CreativeKind) => {
    const fresh = files.map((file) => {
      const draft = blankDraft(file.name)
      return fileMatchesKind(file, uploadKind)
        ? draft
        : { ...draft, status: 'failed' as const, error: t('creativeUpload.wrongFileType') }
    })
    setDrafts((prev) => [...prev, ...fresh])

    const queue = fresh.flatMap((draft, i) =>
      draft.status === 'uploading' ? [{ draft, file: files[i] }] : []
    )
    const worker = async () => {
      for (let job = queue.shift(); job; job = queue.shift()) {
        const { draft, file } = job
        try {
          const asset = await AssetsApi.uploadUnattached(file, (progress) =>
            patchDraft(draft.id, { progress })
          )
          const ready = withLayout({
            ...draft,
            status: 'ready',
            progress: 100,
            asset,
            width: asset.width,
            height: asset.height,
          })
          patchDraft(draft.id, ready)
        } catch (e) {
          patchDraft(draft.id, { status: 'failed', error: (e as Error).message })
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, worker))
  }

  /**
   * The tags in a sheet or pasted text. The server reads the export formats it
   * knows; text it finds nothing in is searched for tags here instead.
   */
  const readTagRows = async (file: File): Promise<ThirdPartyTagRow[]> => {
    const isText = file.type.startsWith('text/') || /\.(txt|csv|tsv)$/i.test(file.name)
    let rows: ThirdPartyTagRow[] = []
    try {
      rows = await CreativesApi.parseThirdPartyFile(file)
    } catch (e) {
      if (!isText) throw e
    }
    if (rows.length || !isText) return rows
    return extractTags(await file.text(), { csv: /\.csv$/i.test(file.name) })
  }

  /** Makes a draft of each tag in each source; `label` stands in for the file name. */
  const readTags = async (sources: { file: File; label: string }[]) => {
    setParsing(true)
    try {
      for (const { file, label } of sources) {
        try {
          const rows = await readTagRows(file)
          if (!rows.length) {
            toast.warning(t('creativeUpload.noTagsFound', { file: label }))
            continue
          }
          const fresh = rows.map((row, i) =>
            withLayout({
              ...blankDraft(label),
              status: 'ready',
              progress: 100,
              tag: row.tag,
              name: row.name || `${nameFromFileName(label)} ${i + 1}`,
              width: row.width ?? 0,
              height: row.height ?? 0,
            })
          )
          setDrafts((prev) => [...prev, ...fresh])
        } catch (e) {
          toast.error(t('creativeUpload.tagFileFailed', { file: label }), {
            description: apiErrorDescription(e),
          })
        }
      }
    } finally {
      setParsing(false)
    }
  }

  // The server tells sheet formats apart by file name, so pasted text goes as a .txt file.
  const readPastedTags = (text: string) =>
    void readTags([
      {
        file: new File([text], 'pasted.txt', { type: 'text/plain' }),
        label: t('creativeUpload.pastedTags'),
      },
    ])

  const onFiles = (files: File[]) => {
    if (!kind) return
    if (kind === 'THIRD_PARTY') void readTags(files.map((file) => ({ file, label: file.name })))
    else void uploadFiles(files, kind)
  }

  const layoutById = useMemo(() => new Map(layouts.map((l) => [l.id, l])), [layouts])
  const toCreate = drafts.filter((d) => d.status !== 'failed')
  const failedCount = drafts.length - toCreate.length
  const uploading = drafts.some((d) => d.status === 'uploading') || parsing
  const blockedCount = toCreate.filter((d) => draftProblem(d, layoutById.get(d.layoutId))).length

  const owner: BulkCreativeOwner | null = lineItemId
    ? { lineItem: lineItemId }
    : advertiserId
      ? { advertiser: advertiserId }
      : null

  const created = useRef(false)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !created.current && drafts.length > 0 && currentLocation.pathname !== nextLocation.pathname
  )

  const backTo = lineItemId
    ? `/advertising/lineitems/${lineItemId}`
    : advertiserId
      ? `/advertising/advertisers/${advertiserId}`
      : '/advertising/creatives'

  const createMutation = useMutation({
    mutationFn: () =>
      CreativesApi.createMany(
        toCreate.map((d) => creativeInputFor(d, layoutById.get(d.layoutId)!, owner!))
      ),
    onSuccess: (result) => {
      if (posthogConfigured) {
        posthog.capture('creatives_bulk_created', {
          count: result.length,
          kind,
          has_line_item: !!lineItemId,
        })
      }
      created.current = true
      queryClient.invalidateQueries({ queryKey: ['creatives'] })
      if (lineItemId) {
        queryClient.invalidateQueries({ queryKey: ['lineitem-creatives', lineItemId] })
        queryClient.invalidateQueries({ queryKey: ['lineitem-preview-assets', lineItemId] })
      }
      toast.success(t('creativeUpload.created', { count: result.length }), { duration: 3000 })
      navigate(backTo)
    },
    onError: (error) => {
      toast.error(t('creativeUpload.createFailed'), {
        description: apiErrorDescription(error),
        duration: 8000,
      })
    },
  })

  const step = !kind ? 0 : drafts.length === 0 ? 1 : 2
  const busy = createMutation.isPending
  const singleCreativeUrl = newObjectUrl(
    'creative',
    { lineItem: lineItemId, advertiser: advertiserId },
    { single: true }
  )

  const header = (
    <div className="ad-page__header">
      <div className="ad-page__title-block">
        <ObjectTypeLabel type={t('objectTypes.creative')} />
        <h1>{t('creativeUpload.title')}</h1>
        <p>{t('creativeUpload.subtitle')}</p>
      </div>
      <div className="ad-page__actions">
        <Button asChild variant="ghost">
          <Link to={singleCreativeUrl}>{t('creativeUpload.singleCreative')}</Link>
        </Button>
      </div>
    </div>
  )

  if (!hasOwner) {
    return (
      <AppShell crumbs={crumbs} showBack>
        <div className="ad-page">
          {header}
          <Card>
            <CardHeader>
              <CardTitle>
                <CardIcon icon={Rows3} />
                {t('creativeUpload.pickLineItem')}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">
                {t('creativeUpload.pickLineItemHelp')}
              </p>
              <SearchSelect
                value=""
                onChange={(id) => id && setSearchParams({ lineitem: id })}
                options={lineItemOptions.map((li) => ({ value: li.id, label: li.name }))}
                placeholder={t('creativeUpload.selectLineItem')}
                icon={Rows3}
                loading={lineItemsLoading}
                className="max-w-md"
              />
            </CardContent>
          </Card>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell crumbs={crumbs} showBack>
      <div className="ad-page">
        {header}

        <ObjectAncestorsList ancestors={ancestors} />

        <WizardSteps
          steps={[
            {
              title: t('creativeUpload.stepKind'),
              summary: kind ? t(`creativeUpload.kind.${kind}`) : undefined,
            },
            { title: t('creativeUpload.stepFiles') },
            { title: t('creativeUpload.stepReview') },
          ]}
          current={step}
          onStepClick={
            busy
              ? undefined
              : (i) => {
                  if (i === 0) {
                    setKind(null)
                    setDrafts([])
                    setSelection([])
                  }
                }
          }
        />

        {product && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Package className="size-4" />
            {t('creativeUpload.productLayouts', { product: product.name })}
          </p>
        )}

        {layoutsLoading ? (
          <FormSkeleton rows={1} columns={4} />
        ) : layouts.length === 0 || CREATIVE_KINDS.every((k) => !layoutCounts[k]) ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Layers />
              </EmptyMedia>
              <EmptyTitle>{t('creativeUpload.noLayoutsTitle')}</EmptyTitle>
              <EmptyDescription>{t('creativeUpload.noLayouts')}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : !kind ? (
          <CreativeKindPicker value={kind} onChange={setKind} layoutCounts={layoutCounts} />
        ) : drafts.length === 0 ? (
          <Card>
            <CardContent>
              <FileDropzone
                accept={KIND_ACCEPT[kind]}
                onFiles={onFiles}
                disabled={parsing}
                title={
                  parsing ? t('creativeUpload.readingTags') : t(`creativeUpload.dropTitle.${kind}`)
                }
                hint={t(`creativeUpload.dropHint.${kind}`)}
              />
              {kind === 'THIRD_PARTY' && (
                <div className="mt-4 flex items-center justify-center gap-3 text-sm text-muted-foreground">
                  {t('creativeUpload.orPaste')}
                  <TagPasteDialog onSubmit={readPastedTags} disabled={parsing} />
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card tone="creative" className="gap-0 pb-0">
            <CardHeader className="pb-4">
              <CardTitle>
                <CardIcon icon={Upload} />
                {t('creativeUpload.fileCount', { count: drafts.length })}
              </CardTitle>
              <CardAction className="flex items-center gap-2">
                {kind === 'THIRD_PARTY' && (
                  <TagPasteDialog onSubmit={readPastedTags} disabled={parsing || busy} />
                )}
                <div className="w-64">
                  <FileDropzone
                    compact
                    accept={KIND_ACCEPT[kind]}
                    onFiles={onFiles}
                    disabled={parsing || busy}
                    title={t('creativeUpload.addMore')}
                  />
                </div>
              </CardAction>
            </CardHeader>
            <BulkEditToolbar
              targets={
                selection.length
                  ? drafts.filter((d) => selection.some((s) => s.id === d.id))
                  : drafts
              }
              selected={selection.length > 0}
              layouts={kindLayouts}
              categoryLabel={categoryLabel}
              onChange={patchDrafts}
              disabled={busy}
            />
            <BulkCreativeTable
              drafts={drafts}
              layouts={kindLayouts}
              categoryLabel={categoryLabel}
              allowedDimensions={product?.allowedDimensions ?? ANY_DIMENSIONS}
              selection={selection}
              onSelectionChange={setSelection}
              onChange={patchDraft}
              onRemove={removeDraft}
              disabled={busy}
            />
          </Card>
        )}

        <div className={`detail-savebar${drafts.length > 0 ? ' is-visible' : ''}`}>
          <div className="detail-savebar__left">
            {busy ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('common.creating')}
              </>
            ) : uploading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('creativeUpload.uploadingFiles')}
              </>
            ) : blockedCount > 0 ? (
              t('creativeUpload.blocked', { count: blockedCount })
            ) : failedCount > 0 ? (
              t('creativeUpload.failedSkipped', { count: failedCount })
            ) : null}
          </div>
          <div className="detail-savebar__actions">
            <Button type="button" variant="ghost" onClick={() => navigate(backTo)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              loading={busy}
              disabled={!owner || uploading || blockedCount > 0 || toCreate.length === 0}
              onClick={() => createMutation.mutate()}
            >
              <Check className="size-4" />
              {t('creativeUpload.create', { count: toCreate.length })}
            </Button>
          </div>
        </div>
      </div>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}
